/* eslint-disable */
import React from 'react';
import HighlightedCardsGrid from '../HighlightedCardsGrid';
import NumberedStepsCarousel from '../NumberedStepsCarousel';
import DoDontCards from '../DoDontCards';
import Quiz from '../Quiz';
import { getCivilIcon } from '../CivilIcons';
import './index.less';

/**
 * civil 课程通用内容块渲染器。
 * 段落 JSON 中通过 blocks 数组声明本页需要的组件与文案，
 * 由各模块共用，避免每个模块重复编写 section 组件。
 */
export interface IContentBlockItem {
  title?: string;
  content?: string;
  text?: string;
  icon?: string;
  active?: boolean;
}

export interface IContentBlock {
  type: 'cards' | 'steps' | 'dodont' | 'quiz';
  /** 需要门控的块必填：既作为 DOM id，也作为段落完成态的组件 key */
  id?: string;
  title?: string;
  subTitle?: string;
  description?: string;
  /** steps 块的版式 */
  variant?: 'standard' | 'simple' | 'case-study' | 'image-left';
  caseLabel?: string;
  caseTitle?: string;
  items?: IContentBlockItem[];
  doColumn?: { title?: string; items?: string[] };
  dontColumn?: { title?: string; items?: string[] };
  question?: string;
  options?: { title: string; hint: string; isCorrect: boolean }[];
}

export interface IContentBlocksProps {
  blocks?: IContentBlock[];
  onComponentComplete?: (componentId: string) => void;
}

const renderBlock = (
  block: IContentBlock,
  index: number,
  onComponentComplete?: (componentId: string) => void
) => {
  const key = block.id || `${block.type}-${index}`;

  switch (block.type) {
    case 'cards':
      return (
        <div key={key} id={block.id} className="content-block content-block-cards">
          <HighlightedCardsGrid
            mainTitle={block.title}
            subTitle={block.subTitle}
            description={block.description}
            items={(block.items || []).map((item) => ({
              title: item.title || '',
              content: item.content || '',
              active: item.active,
              icon: getCivilIcon(item.icon),
            }))}
          />
        </div>
      );

    case 'steps':
      return (
        <div key={key} className="content-block content-block-steps">
          <NumberedStepsCarousel
            type={block.variant || 'simple'}
            id={block.id}
            mainTitle={block.title}
            subTitle={block.subTitle}
            description={block.description}
            caseLabel={block.caseLabel}
            caseTitle={block.caseTitle}
            items={(block.items || []).map((item) => ({ text: item.text || '' }))}
            defaultActive={0}
            onComplete={
              block.id && onComponentComplete
                ? () => onComponentComplete(block.id as string)
                : undefined
            }
          />
        </div>
      );

    case 'dodont':
      return (
        <div key={key} className="content-block content-block-dodont">
          {(block.title || block.description) && (
            <div className="cb-header">
              {block.title && <h2 className="cb-title">{block.title}</h2>}
              {block.description && (
                <p className="cb-description">{block.description}</p>
              )}
            </div>
          )}
          <DoDontCards
            id={block.id}
            doColumn={block.doColumn}
            dontColumn={block.dontColumn}
          />
        </div>
      );

    case 'quiz':
      return (
        <div key={key} id={block.id} className="content-block topic-quiz">
          <Quiz
            title={block.title || ''}
            description={block.description}
            question={block.question}
            options={block.options || []}
            onCompleted={
              block.id && onComponentComplete
                ? () => onComponentComplete(block.id as string)
                : undefined
            }
          />
        </div>
      );

    default:
      return null;
  }
};

const ContentBlocks: React.FunctionComponent<IContentBlocksProps> = ({
  blocks = [],
  onComponentComplete,
}) => (
  <>{blocks.map((block, index) => renderBlock(block, index, onComponentComplete))}</>
);

export default React.memo(ContentBlocks);
